import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';
import BulletPoint from 'dibs-icons/exports/legacy/BulletPoint';
import * as tracking from 'dibs-tracking';
import get from 'lodash.get';
import { DESKTOP, TABLET, MOBILE } from '../../utils/deviceType';
import {
    closeQuickViewModal as closeQuickViewModalDispatch,
    hideQVSpinner as hideQVSpinnerDispatch,
    openQuickViewModal as openQuickViewModalDispatch,
    showQVSpinner as showQVSpinnerDispatch,
    setQuickViewImport as setQuickViewImportDispatch,
} from '../actionCreators/uiActionCreators';
import { Spinner } from 'dibs-elements/exports/Spinner';
import styles from '../styles/catalogHotspot.scss';
import { buildLink } from '../catalogHelpers';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

export const CatalogHotspotComponent = ({
    catalogPage,
    closeQuickViewModal,
    deviceType,
    dimension83,
    dynamicScale,
    hideQVSpinner,
    hotspot,
    isDragging,
    mobileScale,
    quickViewHotspot,
    QuickViewImport,
    quickViewModalOpen,
    scale,
    setQuickViewImport,
    showQVSpinner,
    showQuickViewSpinner,
    tabletScale,
    openQuickViewModal,
    zoomedIn,
    zoomedScale,
}) => {
    let currentScale = zoomedIn ? zoomedScale : scale;
    if (deviceType === TABLET) {
        currentScale = dynamicScale || tabletScale;
    } else if (deviceType === MOBILE) {
        currentScale = dynamicScale || mobileScale;
    }

    let mobileIconScale = 1;
    if (deviceType === MOBILE && hotspot.formType === 'icon') {
        mobileIconScale = 1.5;
    }
    const height = hotspot.dimensions.height * currentScale * mobileIconScale;
    const left = hotspot.dimensions.left * currentScale;
    const top = hotspot.dimensions.top * currentScale;
    const width = hotspot.dimensions.width * currentScale * mobileIconScale;
    const catalogTitleTracking = get(catalogPage, 'title') || 'n/a';
    const catalogPageType = get(catalogPage, 'type') || '';
    const hotspotIsAnIcon = hotspot.formType === 'icon';

    // show spinner around icon only for desktop
    const renderSpinner =
        deviceType === DESKTOP &&
        hotspotIsAnIcon &&
        showQuickViewSpinner &&
        hotspot.hotspotKey === (quickViewHotspot && quickViewHotspot.hotspotKey);

    return (
        <div
            className={styles.hotspotWrapper}
            role="button"
            tabIndex={0}
            style={{
                height: height + 'px',
                left: left + 'px',
                top: top + 'px',
                width: width + 'px',
            }}
            onKeyDown={() => {}}
            onClick={() => {
                // only link when clicked, not when dragging in zoom mode
                if (!isDragging) {
                    // track GA events
                    if (hotspot) {
                        const location = hotspot.formType === 'icon' ? 'embedded' : 'sidebar';
                        const dimension83Tracking =
                            catalogPageType === 'paper:catalog:contemporary'
                                ? hotspot.gaContemporaryTracking
                                : dimension83;
                        // generic event for clicks on all link types
                        tracking.trackEvent({
                            category: 'product catalog',
                            action: 'view product clicked',
                            label: location,
                        });

                        if (hotspot.linkType === 'ITEM') {
                            const {
                                trackingConstants: { ECOM_PRODUCT_CLICK },
                                eventNameConstants: { EVENT_SELECT_ITEM },
                            } = tracking;

                            tracking.trackEcommerce({
                                type: ECOM_PRODUCT_CLICK,
                                eventName: EVENT_SELECT_ITEM,
                                actionField: {
                                    list: `product catalog - ${location}`,
                                    itemId: hotspot.hotspotKey,
                                },
                                products: [
                                    {
                                        category: `${catalogTitleTracking}`,
                                        id: hotspot.hotspotKey,
                                        dimension83: dimension83Tracking,
                                    },
                                ],
                            });

                            // if the modal is supposed to open but the user clicks on the icon again before the quick view data is loaded
                            if (quickViewModalOpen) {
                                hideQVSpinner();
                                closeQuickViewModal();
                                return;
                            }

                            // display loading spinner
                            showQVSpinner();

                            if (QuickViewImport) {
                                // open quick view modal for 'ITEM' type hotspots
                                openQuickViewModal(hotspot);
                            } else {
                                import(
                                    /* webpackChunkName: "quick-view-modal" */
                                    './CatalogQuickViewModal/CatalogQuickViewRenderer'
                                ).then(({ CatalogQuickViewRenderer }) => {
                                    setQuickViewImport(CatalogQuickViewRenderer);

                                    // open quick view modal for 'ITEM' type hotspots
                                    openQuickViewModal(hotspot);
                                });
                            }

                            return;
                        }

                        // open a new tab for other types of hotspots
                        window.open(
                            handleLocaleUrl(
                                buildLink(hotspot.linkType, hotspot.hotspotKey),
                                GLOBAL_CLIENT_ONLY_LOCALE
                            ),
                            '_blank'
                        );
                    }
                }
            }}
        >
            {hotspotIsAnIcon && (
                <div className={styles.pulse}>
                    <BulletPoint className={styles.hotspot} />
                </div>
            )}
            {renderSpinner && <Spinner containerClass={styles.quickViewLoaderIcon} />}
        </div>
    );
};

CatalogHotspotComponent.propTypes = {
    hideQVSpinner: PropTypes.func,
    closeQuickViewModal: PropTypes.func,
    catalogPage: PropTypes.object,
    deviceType: PropTypes.string,
    dimension83: PropTypes.string,
    dynamicScale: PropTypes.number,
    hotspot: PropTypes.object,
    isDragging: PropTypes.bool,
    mobileScale: PropTypes.number,
    quickViewHotspot: PropTypes.object,
    QuickViewImport: PropTypes.func,
    quickViewModalOpen: PropTypes.bool,
    scale: PropTypes.number,
    setQuickViewImport: PropTypes.func,
    showQVSpinner: PropTypes.func,
    showQuickViewSpinner: PropTypes.bool,
    tabletScale: PropTypes.number,
    openQuickViewModal: PropTypes.func,
    zoomedIn: PropTypes.bool,
    zoomedScale: PropTypes.number,
};

const mapStateToProps = ({ qv, ui }) => {
    return {
        dimension83: ui.dimension83,
        isDragging: ui.isDragging,
        mobileScale: ui.mobileScale,
        quickViewHotspot: qv.quickViewHotspot,
        QuickViewImport: ui.QuickViewImport,
        quickViewModalOpen: qv.quickViewModalOpen,
        scale: ui.scale,
        showQuickViewSpinner: qv.showQuickViewSpinner,
        tabletScale: ui.tabletScale,
        zoomedIn: ui.zoomedIn,
        zoomedScale: ui.zoomedScale,
    };
};

const mapDispatchToProps = dispatch => ({
    closeQuickViewModal: () => dispatch(closeQuickViewModalDispatch()),
    hideQVSpinner: () => dispatch(hideQVSpinnerDispatch()),
    openQuickViewModal: hotspot => dispatch(openQuickViewModalDispatch(hotspot)),
    setQuickViewImport: qvImport => dispatch(setQuickViewImportDispatch(qvImport)),
    showQVSpinner: () => dispatch(showQVSpinnerDispatch()),
});

export const CatalogHotspot = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(CatalogHotspotComponent),
    {
        catalogPage: graphql`
            fragment CatalogHotspot_catalogPage on CatalogPage {
                title
                type
            }
        `,
        hotspot: graphql`
            fragment CatalogHotspot_hotspot on CatalogHotspotType {
                hotspotKey
                formType
                linkType
                dimensions {
                    height
                    left
                    top
                    width
                }
                gaContemporaryTracking
            }
        `,
    }
);
